import { devError } from '@/lib/logger';
import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, X, Clock, Calendar, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Requests() {
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseNote, setResponseNote] = useState('');

  useEffect(() => {
    fetchEmployees();
    fetchRequests();
  }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API}/employees`, { credentials: 'include' });
      const data = await res.json();
      setEmployees(data);
    } catch (err) { devError('fetchEmployees', err); }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = `${API}/requests`;
      if (filterStatus !== 'all') {
        url += `?status=${filterStatus}`;
      }
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      setRequests(data);
    } catch (error) {
      toast.error('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (requestId, status) => {
    try {
      const res = await fetch(`${API}/requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, response_note: responseNote })
      });

      if (!res.ok) throw new Error('Erro ao responder');
      
      toast.success(`Solicitação ${status === 'approved' ? 'aprovada' : 'rejeitada'}`);
      setRespondingTo(null);
      setResponseNote('');
      window.dispatchEvent(new Event('requests-updated'));
      fetchRequests();
    } catch (error) {
      toast.error('Erro ao responder solicitação');
    }
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.employee_id === employeeId);
    return emp?.name || '-';
  };

  const requestTypeLabels = {
    vacation: 'Férias',
    shift_swap: 'Troca de Turno',
    absence: 'Aviso de Falta'
  };

  const requestTypeIcons = {
    vacation: <Calendar className="w-5 h-5" />,
    shift_swap: <RefreshCw className="w-5 h-5" />,
    absence: <AlertCircle className="w-5 h-5" />
  };

  const statusLabels = {
    pending: 'Pendente',
    approved: 'Aprovado',
    rejected: 'Rejeitado'
  };

  return (
    <AdminLayout title="Solicitações">
      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="filter-request-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="approved">Aprovadas</SelectItem>
            <SelectItem value="rejected">Rejeitadas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 border border-[#E5E7EB]">
            <Clock className="w-12 h-12 mx-auto mb-4 text-[#9CA3AF]" />
            <p className="text-[#6B7280]">Nenhuma solicitação encontrada</p>
          </div>
        ) : (
          requests.map(req => (
            <div 
              key={req.request_id}
              className={`request-card request-card-${req.status}`}
              data-testid={`request-card-${req.request_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center ${
                    req.status === 'pending' ? 'bg-[#F59E0B]' :
                    req.status === 'approved' ? 'bg-[#059669]' : 'bg-[#DC2626]'
                  } text-white`}>
                    {requestTypeIcons[req.request_type]}
                  </div>
                  <div>
                    <h3 className="font-semibold">{requestTypeLabels[req.request_type]}</h3>
                    <p className="text-sm text-[#6B7280]">{getEmployeeName(req.employee_id)}</p>
                  </div>
                </div>
                <span className={`badge ${
                  req.status === 'pending' ? 'bg-[#F59E0B]' :
                  req.status === 'approved' ? 'bg-[#059669]' : 'bg-[#DC2626]'
                } text-white`}>
                  {statusLabels[req.status]}
                </span>
              </div>

              <div className="mb-4">
                <p className="text-sm">{req.description}</p>
                {(req.start_date || req.end_date) && (
                  <p className="text-sm text-[#6B7280] mt-2">
                    Período: {req.start_date || '-'} até {req.end_date || '-'}
                  </p>
                )}
              </div>

              {req.response_note && (
                <div className="mb-4 p-3 bg-[#F3F4F6]">
                  <p className="text-xs uppercase tracking-widest text-[#6B7280] mb-1">Resposta:</p>
                  <p className="text-sm">{req.response_note}</p>
                </div>
              )}

              {(req.responded_by_name || req.responded_by_role) && (
                <div className="mb-4 text-xs text-[#6B7280] flex flex-wrap items-center gap-2" data-testid={`responded-by-${req.request_id}`}>
                  Respondida por <strong className="text-[#111111]">{req.responded_by_name || '—'}</strong>
                  {req.responded_by_role && (
                    <span
                      className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${
                        req.responded_by_role === 'admin'
                          ? 'bg-[#0029FF] text-white'
                          : req.responded_by_role === 'rh'
                          ? 'bg-[#059669] text-white'
                          : 'bg-[#F59E0B] text-white'
                      }`}
                    >
                      {req.responded_by_role === 'admin' ? 'ADM' : req.responded_by_role === 'rh' ? 'FIN' : 'GEST'}
                    </span>
                  )}
                  {req.responded_at && <span>em {new Date(req.responded_at).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                </div>
              )}

              {req.status === 'pending' && (
                <div>
                  {respondingTo === req.request_id ? (
                    <div className="space-y-3">
                      <Textarea
                        placeholder="Nota de resposta (opcional)"
                        value={responseNote}
                        onChange={(e) => setResponseNote(e.target.value)}
                        data-testid="response-note-input"
                      />
                      <div className="flex items-center gap-2">
                        <Button 
                          onClick={() => handleRespond(req.request_id, 'approved')}
                          className="bg-[#059669] hover:bg-[#059669]/90"
                          data-testid="approve-request-btn"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Aprovar
                        </Button>
                        <Button 
                          onClick={() => handleRespond(req.request_id, 'rejected')}
                          variant="destructive"
                          data-testid="reject-request-btn"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Rejeitar
                        </Button>
                        <Button 
                          variant="ghost"
                          onClick={() => { setRespondingTo(null); setResponseNote(''); }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      variant="outline"
                      onClick={() => setRespondingTo(req.request_id)}
                      data-testid={`respond-btn-${req.request_id}`}
                    >
                      Responder
                    </Button>
                  )}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-[#E5E7EB] text-xs text-[#6B7280]">
                Criado em: {new Date(req.created_at).toLocaleString('pt-BR')}
              </div>
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
}
